import React, { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components';
import { FormGroup } from '../../../contacts/ContactOverview/OrganizationsListing';
import { FieldLabel } from '../../../forms/Components/CreateComponentModal';
import AMSSelect from '../../../../components/AMSSelect';
import { Column, TableConfig } from '../../../../types/types';
import { useTheme } from '../../../../middleware/ThemeContext';
import { useGetContactRolesQuery } from '../../../../api/searchQueryApiSlice';
import { CreateFormTheme, Theme } from '../../../../utils/themes';
import DataTable from '../../../../components/listing/DataTable';
import StyledText from '../../../../components/StyledComponents/StyledText';
import { DropResult } from '@hello-pangea/dnd';
import { LayoutWidget } from '../../../contacts/ContactOverview/DND/dndTypes';

const transformOrgData = (data: any[]) => {

    return data?.map((row) => ({
        id: row.contactId,
        // image: row.signedUrl,
        label: row?.contact?.firstName + " " + row?.contact?.lastName,
        value: row.contactId,
    }));
};

const transformRoleData = (data: any[]) => {
    return data?.map((row) => ({
        label: row.name,
        value: row.id
    }))
};

interface OrganizationOverviewProps {
    organization?: any;
    editingPhase: boolean;
    loading?: boolean;
}
const OrganizationContacts = ({ editingPhase, organization, loading }: OrganizationOverviewProps) => {

    const { theme } = useTheme();
    const [useOrganizationPhone, setUseOrganizationPhone] = useState(false);
    const [editOrganization, setEditOrganization] = useState(false);

    const handleCheckboxChange = () => setUseOrganizationPhone((prev) => !prev);
    const [metadataValues, setMetadataValues] = useState<Record<string, any>>({});
    const [rolesPage, setRolesPage] = useState(1);
    const [allRolesOptions, setAllRolesOptions] = useState<any[]>([]);
    const { data: allRoles, isLoading: isLoadingRol, isFetching: isFetchingRoles } = useGetContactRolesQuery({
        page: rolesPage,
        perPage: 20
    }, {
        refetchOnMountOrArgChange: true
    });

    const rolesHasMore = allRoles ? rolesPage < Math.ceil(allRoles.totalCount / allRoles.perPage) : false;

    useEffect(() => {
        if (allRoles?.roles) {
            const newOptions = transformRoleData(allRoles.roles);
            if (rolesPage === 1) {
                setAllRolesOptions(newOptions);
            } else {
                setAllRolesOptions(prev => [...prev, ...newOptions]);
            }
        }
    }, [allRoles, rolesPage]);

    const my_data = useMemo(
        // () => groupByOrganization(organization?.contactOrgRoles ?? []),
        () => organization?.contactOrganizations ?? [],
        [organization?.contactOrganizations]
    );

    const allContactsOptions = transformOrgData(organization?.contactOrgRoles || []);
    const columns: Column[] = [
        // { id: "firstName", label: "First Name", isSortable: true },
        // { id: "lastName", label: "Last Name", isSortable: true },
        { id: "primaryUser", label: "Name", isSortable: true },
        { id: "email", label: "Email", isSortable: true },
        { id: "role", label: "Roles", isSortable: false, isTooltip: true, TooltipText: 'These roles are only for this organization', isTag: true },


    ];
    

    const data = (my_data ?? []).map((data: any) => (
        {
            // firstName: data?.contacts?.firstName,
            // lastName: data?.contacts?.lastName,
            contactId: data?.contact?.id,
            contact_id: data?.contact?.id,
            contactFirstName: data?.contact?.firstName,
            contactLastName: data?.contact?.lastName,
            contactSignedUrl: data?.contact?.signedUrl,
            // primaryUser: data?.contacts?.firstName+' '+data?.contacts?.lastName,
            email: data?.contact?.email,
            role: data?.contactOrgRoles?.map((row: any) => row?.role?.name)?.join(", "),
            preferredAddress: data?.contacts?.address ?? "address not found",
            preferredPhone: data?.contacts?.phone ?? "phone number",
        }));

    const STORAGE_KEY = `tableConfig_companies`;
    const [tableConfig, setTableConfig] = useState<TableConfig>(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved
            ? JSON.parse(saved)
            : {
                visibleColumns: columns.filter(col => col.id !== "id").map(col => col.id),
                page: 1,
                perPage: 10,
                sort: undefined,
                filters: {},
                query: "",
                columnOrder: columns.map(col => col.id),
            };
    });

    const handleDragEnd = useCallback(
        (result: DropResult) => {
            if (!result.destination) return;
            const newColumnOrder = Array.from(tableConfig.columnOrder);
            const [removed] = newColumnOrder.splice(result.source.index, 1);
            newColumnOrder.splice(result.destination.index, 0, removed);
            setTableConfig(prev => ({ ...prev, columnOrder: newColumnOrder }));
        },
        [tableConfig.columnOrder]
    );

    const addresses = [
        { id: '1', title: 'Main Office London', address: '1452 Willow Creek Ave, Suite 300, San Diego, CA 92101', isPrimary: true },
        { id: '2', title: 'Main Office London', address: '1452 Willow Creek Ave, Suite 300, San Diego, CA 92101', addedBy: 'l2' },
        { id: '3', title: 'Main Office London', address: '1452 Willow Creek Ave, Suite 300, San Diego, CA 92101' },
    ];

    const addOrgRoleRow = () => {
        setMetadataValues(prev => {
            const roles = Array.isArray(prev.contacts) ? [...prev.contacts] : [];
            return { ...prev, contacts: [...roles, { contactsId: "", rolesId: "" }] };
        });
    };

    const removeOrgRoleRow = (rowIndex: number) => {
        setMetadataValues(prev => {
            const roles = Array.isArray(prev.contacts) ? [...prev.contacts] : [];
            if (roles.length <= 1) return prev;
            roles.splice(rowIndex, 1);
            return { ...prev, contacts: roles };
        });
    };



    const handleOrgRoleChange = (rowIndex: number, field: "org" | "role", value: any) => {
        setMetadataValues(prev => {
            const roles = Array.isArray(prev.contacts) ? [...prev.contacts] : [{ contactsId: "", rolesId: "" }];
            if (!roles[rowIndex]) roles[rowIndex] = { contactsId: "", rolesId: "" };

            if (field === "org") {
                roles[rowIndex].contactsId = value;

                roles[rowIndex].rolesId = "";
            } else {
                roles[rowIndex].rolesId = value;
            }

            return { ...prev, contacts: roles };
        });
    };



    useEffect(() => {
        if (my_data && my_data.length > 0) {

            const initialMetadataValues = {
                contacts: my_data.map((field: any) => ({
                    contactsId: field?.id,
                    rolesId: field?.role?.map((row: any) => row.id),
                })),
            };

            setMetadataValues(initialMetadataValues);
        } else {

            setMetadataValues({
                contacts: [
                    {
                        contactsId: "",
                        rolesId: [],
                    },
                ],
            });
        }
    }, [my_data]);
    

    return (
        <div
            // theme={theme} editingPhase={editingPhase} 
            className="mt-2">
            {/* <div className="d-flex align-items-center justify-content-between px-3"> */}
            {/* <StyledText fontSize="16px" fontWeight={700} textColor={theme.CorporateTheme.colors.headerColor} className="mb-0 py-3">Contacts</StyledText> */}
            {/* <EditIcon color={theme.CorporateTheme.colors.textSecondary} className="cursor-pointer" /> */}
            {/* <EditIcon
                    className="cursor-pointer"
                    onClick={() => setEditOrganization(true)}
                    color={theme.CorporateTheme.colors.textSecondary}
                  /> */}
            {/* {editOrganization ? (
                    <div className="d-flex align-items-center gap-2 ">

                      <CrossIcon
                        // className={`${isLoadingUpdateOrg && 'class-disable'} cursor-pointer`}
                        onClick={() => setEditOrganization(false)}
                        color={theme.CorporateTheme.colors.textSecondary}
                      />
                    </div>
                  ) : (
                    <EditIcon
                      className="cursor-pointer"
                      onClick={() => setEditOrganization(true)}
                      color={theme.CorporateTheme.colors.textSecondary}
                    />
                  )} */}
            {/* </div> */}
            {/* <StyledHr theme={theme} /> */}
            <div>
                {!editOrganization ? <div className="mb-3 px-3">
                    <DataTable
                        noAction
                        columns={columns}
                        data={data}
                        columnOrder={tableConfig.columnOrder}
                        visibleColumns={tableConfig.visibleColumns}
                        sort={tableConfig.sort}
                        title='Contacts Listing'
                        noDataTitle="No Contact Found"
                        noDataSubtitle="Your Contact list is currently empty. Start adding new contacts to keep everything organized and easy to find."
                        onDragEnd={handleDragEnd}
                        loading={loading ?? false}
                        perPage={tableConfig.perPage}
                        onAction={() => { }}
                        minWidth="1000px"
                        hideActionColumn={true}
                    />
                </div> :
                    <div className="px-3">
                        {metadataValues?.contacts?.map((row: any, index: any) => (
                            <div className="row" key={index}>
                                {/* Organization */}
                                <div className="col">
                                    <FormGroup>
                                        <FieldLabel theme={theme} className="mb-2">Contact Name</FieldLabel>
                                        <AMSSelect
                                            placeholder='Select Contact'
                                            staticOptions={allContactsOptions || []}
                                            value={(() => {
                                                const options = allContactsOptions || [];
                                                return options.find((o: any) => String(o.value) === String(row.contactsId)) ?? null;
                                            })()}
                                            onChange={(opt: any) => handleOrgRoleChange(index, "org", opt?.value ?? '')}
                                            isSearchable
                                            isDisabled
                                            includeAddButton
                                            addButtonText="Create Organization"
                                            // onAddButtonClick={(val: string) => addOrganization(val)}
                                            includeId
                                            includeImage
                                            menuPosition="fixed"
                                        />

                                        {/* {metadataErrors["organizationRoles_org_" + index] && (
                                            <ErrorText theme={theme}>
                                            {metadataErrors["organizationRoles_org_" + index]}
                                            </ErrorText>
                                        )} */}
                                    </FormGroup>
                                </div>
                                {row.contactsId && (
                                    <div className="col">
                                        <FormGroup>
                                            <FieldLabel theme={theme} className="mb-2">
                                                Contact Roles
                                            </FieldLabel>
                                            <AMSSelect
                                                placeholder='Select Roles'
                                                staticOptions={allRolesOptions}
                                                isMulti
                                                isSearchable
                                                isDisabled
                                                value={(() => {
                                                    const selectedIds = Array.isArray(row.rolesId) ? row.rolesId : [];
                                                    return (allRolesOptions ?? []).filter((opt: any) =>
                                                        selectedIds.some((id: any) => String(id) === String(opt.value))
                                                    );
                                                })()}
                                                onChange={(opts: any) => {
                                                    const selected = Array.isArray(opts) ? opts : [];
                                                    handleOrgRoleChange(index, "role", selected.map((o: any) => o?.value));
                                                }}
                                                onMenuScrollToBottom={() => {
                                                    if (rolesHasMore && !isFetchingRoles) {
                                                        setRolesPage(prev => prev + 1);
                                                    }
                                                }}
                                                menuPosition="fixed"
                                            />
                                            {/* {metadataErrors["organizationRoles_role_" + index] && (
                                                        <ErrorText theme={theme}>
                                                            {metadataErrors["organizationRoles_role_" + index]}
                                                        </ErrorText>
                                                        )} */}
                                        </FormGroup>
                                    </div>
                                )}


                                {/* Add / Remove */}
                                {/* <div className="col-2 d-flex align-items-center gap-2">
                          {index === metadataValues?.contacts?.length - 1 ? (
                            <Button type='button' theme={theme} $variant="customize" onClick={addOrgRoleRow}><PlusIcon /> </Button>
                          ) : (
                            <Button type="button" theme={theme} $variant="customize" onClick={() => removeOrgRoleRow(index)}>-</Button>
                          )}
                        </div> */}
                            </div>
                        ))}
                    </div>}
            </div>
        </div>
    )
}


const StyledBox = styled('div')<{ editingPhase?: boolean }>(
    ({ theme, editingPhase }) => ({
        backgroundColor: editingPhase
            ? theme.CorporateTheme?.colors?.bannerBG
            : theme.CorporateTheme?.colors?.background,
        borderRadius: '5px',
        border: `1px solid ${theme.CorporateTheme?.colors?.border}`,
        boxShadow: '0px 2px 10px 0px #0000000d',
        marginBottom: '10px',
    })
);

const LeftView = styled.div`
width: 70%;
display: flex;
  `
const RightView = styled.div`
display: flex;
flex-direction: column;
gap: 10px;
width: 30%;
  `


export const StyledHr = styled('div')(({ theme }) => ({
    marginBottom: '10px',
    color: theme.CorporateTheme?.colors?.border,
    borderBottom: `0.5px solid ${theme.CorporateTheme?.colors?.border}`,
}));

export const StyledVr = styled.div`
  border-right: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  color: ${({ theme }) => theme.CorporateTheme.colors.border};
  height: 50px;
`;

export const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${CreateFormTheme.spacing.sm};
  cursor: pointer;
  font-weight: 500;
  color: ${CreateFormTheme.colors.placeHolder};

  input {
    margin: 0;
  }
`;

const StyledHeightlightBar = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
  padding: 10px;
  max-width: 28%;
  min-width: 28%;
  margin: 4px 10px;
  text-align: center;
`;

const StyledAddressBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 4px;
  padding: 10px;
  margin: 4px 10px;
`;

const StyledContactTableHeader = styled.div<{ theme?: Theme }>`
  background: ${({ theme }) => theme.CorporateTheme.colors.headerBG};
  height: 45px;
`

const DescriptionArea = styled.textarea<{ $editingPhase?: boolean }>`
  color: ${CreateFormTheme.colors.placeHolder};
  font-weight: 500;
  font-size: 14px;
  border: ${props => props.$editingPhase ? '1px' : '0px'} solid ${CreateFormTheme.colors.inputBorders};
  border-radius: ${CreateFormTheme.borderRadius};
  font-family: 'Raleway', sans-serif;
  min-height: 100px;
  width: 100%;
  &:focus {
    outline: 2px solid ${CreateFormTheme.colors.border};
  }
  &::placeholder {
    color: ${CreateFormTheme.colors.placeHolder};
    opacity: 1;
  }
`;

// Skeleton
export const SkeletonCell = styled.div`
  width: 100%;
  height: 37px;
  border-radius: 4px;
  background: linear-gradient(90deg, #e8e8e8 25%, #f5f5f5 50%, #e8e8e8 75%);
  background-size: 200% 100%;
  animation: shimmer 0.5s infinite;
  @keyframes shimmer {
    0% { background-position: -200% 0; }
    100% { background-position: 200% 0; }
  }
`;

export default OrganizationContacts
